'use client';

import { useState } from 'react';
import { X } from 'lucide-react';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer';
import type { MarketingLandingCopy } from '@/i18n/marketing-landing-copy';
import { cn } from '@/lib/utils';
import type { MarketingNavGroup } from './content';
import { MobileNavPanel } from './landing-nav-mobile';
import { NavMenuPanel } from './landing-nav-menu';
import { SectionShell } from './section-shell';

export type MarketingMenu = 'solutions' | 'resources';

export function MobileMenuIcon() {
  return (
    <span aria-hidden="true" className="flex w-5 flex-col gap-1.5 text-current">
      <span className="h-px w-full bg-current" />
      <span className="h-px w-full bg-current" />
    </span>
  );
}

export function DesktopNavDrawer({
  closeLabel,
  groups,
  label,
  layout,
  menu,
  onOpenMenuChange,
  openMenu,
  portalContainer,
}: {
  closeLabel: string;
  groups: Array<MarketingNavGroup>;
  label: string;
  layout: 'columns' | 'centered';
  menu: MarketingMenu;
  onOpenMenuChange: (menu: MarketingMenu | null) => void;
  openMenu: MarketingMenu | null;
  portalContainer: HTMLElement | null;
}) {
  const open = openMenu === menu;

  return (
    <Drawer
      direction="top"
      open={open}
      shouldScaleBackground={false}
      onOpenChange={(nextOpen) => onOpenMenuChange(nextOpen ? menu : null)}
    >
      <DrawerTrigger asChild>
        <button
          type="button"
          data-marketing-nav-trigger=""
          className="text-marketing-fg hover:text-marketing-fg/80 focus-visible:outline-marketing-accent-strong cursor-pointer text-sm font-medium [letter-spacing:-0.01em] transition-colors duration-200 outline-none focus-visible:outline-2 focus-visible:outline-offset-4"
        >
          {label}
        </button>
      </DrawerTrigger>

      <DrawerContent
        portalContainer={portalContainer}
        overlayClassName="bg-black/60 z-40!"
        className="bg-marketing-bg tablet:flex z-50! hidden w-full gap-0 overflow-y-auto p-0 shadow-none data-[vaul-drawer-direction=top]:top-0 data-[vaul-drawer-direction=top]:mb-0! data-[vaul-drawer-direction=top]:max-h-dvh! data-[vaul-drawer-direction=top]:rounded-none!"
        onPointerDownOutside={(event) => {
          const target = event.target;
          if (
            target instanceof Element &&
            target.closest('[data-marketing-nav-trigger]')
          ) {
            event.preventDefault();
          }
        }}
      >
        <DrawerHeader className="sr-only">
          <DrawerTitle>{label}</DrawerTitle>
        </DrawerHeader>
        <DrawerClose asChild>
          <button
            type="button"
            aria-label={closeLabel}
            className="bg-marketing-panel text-marketing-panel-fg focus-visible:outline-marketing-accent-strong hover:bg-marketing-panel/80 tablet:flex absolute top-5 right-4 z-10 hidden size-8 cursor-pointer items-center justify-center rounded-sm transition-colors outline-none focus-visible:outline-2 focus-visible:outline-offset-2"
          >
            <X className="size-5" aria-hidden="true" strokeWidth={3} />
          </button>
        </DrawerClose>
        <SectionShell className="pt-10">
          <nav aria-label={label}>
            <NavMenuPanel
              groups={groups}
              layout={layout}
              onNavigate={() => onOpenMenuChange(null)}
            />
          </nav>
        </SectionShell>
      </DrawerContent>
    </Drawer>
  );
}

export function MobileNavDrawer({
  copy,
  portalContainer,
}: {
  copy: MarketingLandingCopy;
  portalContainer: HTMLElement | null;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Drawer
      direction="right"
      open={open}
      shouldScaleBackground={false}
      onOpenChange={setOpen}
    >
      <DrawerTrigger asChild>
        <button
          type="button"
          aria-label={
            open ? copy.brand.closeMenuLabel : copy.brand.openMenuLabel
          }
          className={cn(
            'focus-visible:outline-marketing-accent-strong tablet:hidden -mt-1 flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-sm outline-none focus-visible:outline-2 focus-visible:outline-offset-2',
            open
              ? 'bg-marketing-chip text-marketing-chip-fg'
              : 'bg-marketing-panel text-marketing-panel-fg'
          )}
        >
          {open ? (
            <X className="size-5" aria-hidden="true" />
          ) : (
            <MobileMenuIcon />
          )}
        </button>
      </DrawerTrigger>

      <DrawerContent
        portalContainer={portalContainer}
        overlayClassName="bg-marketing-ink/70 z-40 tablet:hidden"
        className="bg-marketing-bg tablet:hidden z-50 h-dvh w-full gap-0 overflow-hidden border-0 p-0 shadow-none data-[vaul-drawer-direction=right]:w-full data-[vaul-drawer-direction=right]:sm:max-w-none"
      >
        <DrawerHeader className="sr-only">
          <DrawerTitle>{copy.brand.primaryNavLabel}</DrawerTitle>
        </DrawerHeader>
        <div className="flex shrink-0 items-center justify-between px-3 pt-[max(0.75rem,env(safe-area-inset-top))]">
          <div />
          <DrawerClose asChild>
            <button
              type="button"
              aria-label={copy.brand.closeMenuLabel}
              className="bg-marketing-chip text-marketing-chip-fg focus-visible:outline-marketing-accent-strong flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-sm outline-none focus-visible:outline-2 focus-visible:outline-offset-2"
            >
              <X className="size-5" aria-hidden="true" />
            </button>
          </DrawerClose>
        </div>
        <div className="scrollbar-hide min-h-0 flex-1 overflow-y-auto px-2 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
          <nav aria-label={copy.brand.primaryNavLabel}>
            <MobileNavPanel copy={copy} onNavigate={() => setOpen(false)} />
          </nav>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
